import React, { useEffect, useRef } from 'react';
import {
  Animated,
  Easing,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { fpColors } from '../styles/theme';

/**
 * Absolutely-positioned scan overlay that sits on top of an image while an
 * upload / AI analysis is running.
 *
 * Visuals
 * - A translucent horizontal bar with a bright purple edge line that
 *   sweeps top→bottom→top on a 2.5s loop.
 * - Four corner brackets to imply a viewfinder / AI focus area.
 * - A "status pill" in the top-right with a pulsing dot and context copy
 *   — "AI SCANNING..." while active, "AI ANALYSIS COMPLETE" when done.
 */
interface ScanOverlayProps {
  /** Height of the parent image (needed to animate the line within it). */
  height: number;
  /** When false, the animations stop and the pill switches to the done state. */
  active: boolean;
  /** Copy shown in the status pill while active (default "AI SCANNING..."). */
  activeLabel?: string;
  /** Copy shown when active is false (default "AI ANALYSIS COMPLETE"). */
  doneLabel?: string;
}

export const ScanOverlay: React.FC<ScanOverlayProps> = ({
  height,
  active,
  activeLabel = 'AI SCANNING…',
  doneLabel = 'AI ANALYSIS COMPLETE',
}) => {
  const sweep = useRef(new Animated.Value(0)).current;
  const pulse = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!active) return;
    const sweepLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(sweep, {
          toValue: 1,
          duration: 1600,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(sweep, {
          toValue: 0,
          duration: 1600,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    sweepLoop.start();
    return () => sweepLoop.stop();
  }, [active, sweep]);

  useEffect(() => {
    const pulseLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 700,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 0,
          duration: 700,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
      ]),
    );
    pulseLoop.start();
    return () => pulseLoop.stop();
  }, [pulse]);

  // Translate the sweep line across the image. `height` is the container
  // height; the line itself is ~60px tall so we keep a margin to avoid
  // clipping at the edges.
  const lineHeight = 54;
  const sweepMax = Math.max(0, height - lineHeight);
  const translateY = sweep.interpolate({
    inputRange: [0, 1],
    outputRange: [0, sweepMax],
  });
  const pulseOpacity = pulse.interpolate({
    inputRange: [0, 1],
    outputRange: [0.3, 1],
  });

  return (
    <View style={styles.root} pointerEvents="none">
      {/* Subtle darkening tint so the sweep reads cleanly */}
      <View style={styles.tint} />

      {/* The sweep bar — rendered only when the scan is active */}
      {active && (
        <Animated.View
          style={[
            styles.sweep,
            { height: lineHeight, transform: [{ translateY }] },
          ]}
        >
          <View style={styles.sweepGradientTop} />
          <View style={styles.sweepLine} />
          <View style={styles.sweepGradientBottom} />
        </Animated.View>
      )}

      {/* Viewfinder corner brackets */}
      <View style={[styles.corner, styles.cornerTL]} />
      <View style={[styles.corner, styles.cornerTR]} />
      <View style={[styles.corner, styles.cornerBL]} />
      <View style={[styles.corner, styles.cornerBR]} />

      {/* Status pill */}
      <View style={styles.statusPill}>
        <Animated.View
          style={[
            styles.statusDot,
            !active && styles.statusDotDone,
            { opacity: active ? pulseOpacity : 1 },
          ]}
        />
        <Text style={styles.statusText}>{active ? activeLabel : doneLabel}</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    ...StyleSheet.absoluteFillObject,
    overflow: 'hidden',
  },
  tint: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(125, 52, 183, 0.05)', // fpColors.primary at 5%
  },
  sweep: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sweepGradientTop: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: 24,
    backgroundColor: 'rgba(125, 52, 183, 0.08)',
  },
  sweepGradientBottom: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 24,
    backgroundColor: 'rgba(125, 52, 183, 0.08)',
  },
  sweepLine: {
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    height: 2,
    backgroundColor: fpColors.primary,
    shadowColor: fpColors.primary,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.8,
    shadowRadius: 10,
    elevation: 4,
  },
  corner: {
    position: 'absolute',
    width: 22,
    height: 22,
    borderColor: fpColors.primary,
  },
  cornerTL: {
    top: 12,
    left: 12,
    borderTopWidth: 2,
    borderLeftWidth: 2,
    borderTopLeftRadius: 4,
  },
  cornerTR: {
    top: 12,
    right: 12,
    borderTopWidth: 2,
    borderRightWidth: 2,
    borderTopRightRadius: 4,
  },
  cornerBL: {
    bottom: 12,
    left: 12,
    borderBottomWidth: 2,
    borderLeftWidth: 2,
    borderBottomLeftRadius: 4,
  },
  cornerBR: {
    bottom: 12,
    right: 12,
    borderBottomWidth: 2,
    borderRightWidth: 2,
    borderBottomRightRadius: 4,
  },
  statusPill: {
    position: 'absolute',
    top: 14,
    right: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    borderWidth: 1,
    borderColor: `${fpColors.primary}33`,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
    elevation: 3,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: fpColors.primary,
  },
  statusDotDone: {
    backgroundColor: '#22C55E', // success green
  },
  statusText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    letterSpacing: 0.8,
    color: fpColors.primary,
  },
});
